import * as Phaser from 'phaser';

const SNOW_TEXTURE_KEY = '__frostmere_snow__';
const BREATH_TEXTURE_KEY = '__frostmere_breath__';
const SMOKE_TEXTURE_KEY = '__frostmere_smoke__';

export function installSnowTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(SNOW_TEXTURE_KEY)) return;
  const SIZE = 6;
  const HALF = SIZE / 2;
  const gfx = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let r = HALF; r >= 1; r--) {
    const alpha = (1 - r / HALF) ** 1.5;
    gfx.fillStyle(0xffffff, alpha);
    gfx.fillCircle(HALF, HALF, r);
  }
  gfx.generateTexture(SNOW_TEXTURE_KEY, SIZE, SIZE);
  gfx.destroy();
}

export function installBreathTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(BREATH_TEXTURE_KEY)) return;
  const SIZE = 16;
  const HALF = SIZE / 2;
  const gfx = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let r = HALF; r >= 1; r--) {
    const alpha = (1 - r / HALF) ** 2 * 0.6;
    gfx.fillStyle(0xd0e8f0, alpha);
    gfx.fillCircle(HALF, HALF, r);
  }
  gfx.generateTexture(BREATH_TEXTURE_KEY, SIZE, SIZE);
  gfx.destroy();
}

export function installSmokeTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(SMOKE_TEXTURE_KEY)) return;
  const SIZE = 20;
  const HALF = SIZE / 2;
  const gfx = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let r = HALF; r >= 1; r--) {
    const alpha = (1 - r / HALF) ** 2 * 0.5;
    gfx.fillStyle(0xc0b8a8, alpha);
    gfx.fillCircle(HALF, HALF, r);
  }
  gfx.generateTexture(SMOKE_TEXTURE_KEY, SIZE, SIZE);
  gfx.destroy();
}

const SNOW_DEPTH = 150;

export function createSnowfall(
  scene: Phaser.Scene,
  density: number = 1.0,
): Phaser.GameObjects.Particles.ParticleEmitter {
  const w = scene.scale.width;
  const h = scene.scale.height;
  const emitter = scene.add.particles(0, 0, SNOW_TEXTURE_KEY, {
    x: { min: -20, max: w + 20 },
    y: -10,
    speedX: { min: -15, max: 15 },
    speedY: { min: 20, max: 55 },
    alpha: { min: 0.3, max: 0.8 },
    scale: { min: 0.4, max: 1.2 },
    lifespan: { min: 6000, max: 12000 },
    frequency: Math.round(120 / density),
    quantity: 1,
    maxAliveParticles: Math.round(60 * density),
    rotate: { min: 0, max: 360 },
    blendMode: Phaser.BlendModes.NORMAL,
    deathZone: {
      type: 'onEnter',
      source: { contains: (_x: number, y: number) => y > h + 10 },
    },
    advance: 4000,
  });
  emitter.setScrollFactor(0);
  emitter.setDepth(SNOW_DEPTH);
  return emitter;
}

const BREATH_DEPTH = 101;

export function createBreathMist(
  scene: Phaser.Scene,
  walker: Phaser.GameObjects.Sprite,
): Phaser.GameObjects.Particles.ParticleEmitter {
  const emitter = scene.add.particles(0, 0, BREATH_TEXTURE_KEY, {
    speedX: { min: 8, max: 20 },
    speedY: { min: -12, max: -4 },
    alpha: { start: 0.5, end: 0 },
    scale: { start: 0.3, end: 0.9 },
    lifespan: { min: 600, max: 1000 },
    frequency: 1800,
    quantity: 2,
    maxAliveParticles: 6,
    blendMode: Phaser.BlendModes.NORMAL,
  });
  emitter.startFollow(walker, 12, -walker.displayHeight * 0.75);
  emitter.setDepth(BREATH_DEPTH);
  return emitter;
}

export { SNOW_TEXTURE_KEY, BREATH_TEXTURE_KEY, SMOKE_TEXTURE_KEY };
